/**
 * The workflow editor: the canvas, the block cards, the panel, and the run controls.
 *
 * One half of `AGENT_COPY`. The shape and the rules are documented on the
 * barrel in `../agent-copy.ts`; this file only holds entries.
 *
 * The vocabulary: a block on this canvas reads 节点; workflow 工作流, run and
 * execution 运行, version 版本, canvas 画布, variable 变量, deployment 部署.
 */
export const WORKFLOWS: Record<string, string> = {
	"Workflow chat history": "流程对话记录",
	"Describe what you want to change in this workflow.": "描述你想怎样修改这个流程。",
	"Test chat": "测试对话",
	"The design assistant is unavailable while this workflow is read-only.": "当前工作流为只读，暂时无法使用设计助手。",

	// -- the workflow's schedule (Ondit's own control, not upstream's)
	Schedule: "定时",
	"Workflow schedule": "工作流定时",
	"Runs the saved workflow revision. Later canvas edits do not change this schedule. Model calls use Ondit credits.":
		"按已保存的工作流版本运行。之后在画布上的修改不会影响此定时任务；模型调用会消耗 Ondit 积分。",
	"Cron (minute hour day month weekday)": "Cron（分 时 日 月 周）",
	Timezone: "时区",
	"Credit ceiling per run": "单次运行积分上限",
	"No ceiling": "不设上限",
	"Start input (JSON)": "启动输入（JSON）",
	"Create schedule": "创建定时任务",
	"Save schedule": "保存定时任务",
	"Saving replaces this node’s current schedule.": "保存后将替换此节点当前的定时任务。",
	"These are the Schedule node’s settings. Changing them here changes the node.":
		"这些是定时节点的设置，在此修改会同步到节点。",
	"This workflow starts from its Start node, so these settings belong to this schedule only.":
		"此工作流从开始节点启动，这些设置只属于这个定时任务。",
	"Saved schedule": "已保存的定时任务",
	"Not saved as a schedule yet. Save it from Schedule above the canvas.":
		"尚未保存为定时任务。请通过画布上方的“定时”保存。",
	"The saved schedule differs from these settings. Save it again to apply them.":
		"已保存的定时任务与当前设置不同，重新保存后生效。",
	"Next runs": "接下来的运行",
	"Enter a credit ceiling from 1 to 5,000": "请输入 1 至 5,000 之间的积分上限",
	"Start input must be a JSON object": "启动输入必须是 JSON 对象",
	"Reload the workflow": "请刷新工作流",
	// -- how a schedule reads, wherever it is shown
	"Every minute": "每分钟",
	"Every {n} minutes": "每 {n} 分钟",
	"Every hour at minute {minute}": "每小时第 {minute} 分钟",
	"Every day at {time}": "每天 {time}",
	"Every Monday at {time}": "每周一 {time}",
	"Every Tuesday at {time}": "每周二 {time}",
	"Every Wednesday at {time}": "每周三 {time}",
	"Every Thursday at {time}": "每周四 {time}",
	"Every Friday at {time}": "每周五 {time}",
	"Every Saturday at {time}": "每周六 {time}",
	"Every Sunday at {time}": "每周日 {time}",
	"Every month on day {day} at {time}": "每月 {day} 日 {time}",
	"{schedule} ({timezone})": "{schedule}（{timezone}）",
	"Cron {expression}": "Cron {expression}",
	"Choose how often the schedule runs": "请选择运行频率",
	"Choose an interval that divides the hour: 1, 2, 3, 4, 5, 6, 10, 12, 15, 20 or 30 minutes":
		"请选择能整除一小时的间隔：1、2、3、4、5、6、10、12、15、20 或 30 分钟",
	"Enter a minute from 0 to 59": "请输入 0–59 之间的分钟",
	"Choose a time": "请选择时间",
	"Choose a day of the week": "请选择星期几",
	"Enter a day of the month from 1 to 31": "请输入 1–31 之间的日期",
	// -- canvas controls: the floating bar at the canvas floor
	Mover: "抓手",
	Pointer: "指针",
	Undo: "撤销",
	Redo: "重做",
	"Fit to View": "适应视图",
	"Hide canvas controls": "隐藏画布控件",

	// -- the block's own action bar
	"Block running": "节点运行中",
	"Connection lost - please refresh": "连接已断开 — 请刷新",
	"Read-only mode": "只读模式",
	"Stop workflow": "停止工作流",
	"Run block": "运行节点",
	Stop: "停止",
	Run: "运行",
	"Block is locked": "节点已锁定",
	"Run previous blocks first": "请先运行前面的节点",
	"Parent container is disabled": "所在容器已停用",
	"Parent container is locked": "所在容器已锁定",
	Lock: "锁定",
	Unlock: "解锁",
	Duplicate: "创建副本",
	"Remove from Subflow": "移出子流程",
	"Note color": "便签颜色",
	Color: "颜色",
	White: "白色",
	"Light gray": "浅灰",
	Graphite: "石墨灰",

	// -- the canvas context menu
	Paste: "粘贴",
	"Add Block": "添加节点",
	"Auto-layout": "自动排布",
	"Unlock blocks to use auto-layout": "解锁节点后才能自动排布",
	"Lock workflow": "锁定工作流",
	"Unlock workflow": "解锁工作流",
	"Search and replace": "查找和替换",
	"Open Logs": "打开日志",
	"Open Variables": "打开变量",
	"Close Variables": "关闭变量",
	"Open Chat": "打开对话",
	"Close Chat": "关闭对话",

	// -- the block context menu
	Copy: "复制",
	Cut: "剪切",
	"Toggle Enabled": "切换启用状态",
	"Toggle Lock": "切换锁定状态",
	"Parent is disabled": "上级已停用",
	"Parent is locked": "上级已锁定",
	"Add Image": "添加图片",
	"Open Editor": "打开编辑器",
	"Run from block": "从此节点开始运行",
	"Run until block": "运行到此节点",

	// -- the diff controls, when Copilot proposes a canvas change
	Accept: "采用",
	"Accept changes (⇧⌘⏎)": "采用改动（⇧⌘⏎）",
	Reject: "放弃",
	"Reject changes": "放弃改动",

	// -- the canvas error boundary
	"Something went wrong": "出错了",
	"An unexpected error occurred. Please try again or refresh the page.": "发生了意外错误，请重试或刷新页面。",
	"Try again": "重试",

	// -- the toolbar: the block palette
	Toolbar: "工具栏",
	"Search toolbar": "搜索工具栏",
	Triggers: "触发器",
	"Events that start a workflow": "启动工作流的事件",
	"Core Blocks": "核心节点",
	"Core building blocks for agent logic": "构建智能体逻辑的基础节点",
	"Custom Blocks": "自定义节点",
	"Workflows published as reusable blocks across your organization": "已发布为组织内可复用节点的工作流",
	Integrations: "集成",
	"Connect apps": "接入",
	"Connect agents to external services": "把智能体接到外部服务",
	"Add to canvas": "添加到画布",
	"View documentation": "查看文档",

	// -- the empty canvas
	"New Agent": "新建智能体",
	Logs: "日志",
	Tasks: "任务",
	"Search Blocks": "搜索节点",

	// -- the wand: generate a field's value with a model
	"Toggle terminal": "展开或收起控制台",
	"Describe what you want to generate...": "描述你想生成的内容…",
	"Generating...": "生成中…",
	"Prompt cannot be empty.": "提示词不能为空。",
	"Wand is not enabled.": "此处未启用 AI 生成。",
	"Generation failed": "生成失败",
	"Usage limit reached": "已达用量上限",
	Upgrade: "升级",

	// -- the panel's header, its menu and its tabs
	"Workflow panel": "工作流面板",
	"Auto layout": "自动排布",
	"Arrange vertically": "改为纵向排布",
	"Arrange horizontally": "改为横向排布",
	"Export workflow": "导出工作流",
	"Duplicate workflow": "复制工作流",
	"Workflow is locked at the row or folder level — release it from the workflow notification or folder menu":
		"工作流已在列表行或文件夹层级被锁定 — 请在工作流通知或文件夹菜单中解除",
	Editor: "编辑器",
	"New Chat": "新建对话",
	Recent: "最近",
	"Resize panel": "调整面板宽度",
	"Delete Workflow": "删除工作流",
	"this workflow": "这个工作流",
	"All associated blocks, executions, and configuration will be removed.": "其中的节点、运行记录和配置都会一并删除。",

	// -- the editor tab: one block's fields
	"Select a block to edit": "选择一个节点来编辑",
	"Unlock block": "解锁节点",
	"Ancestor container is locked": "上级容器已锁定",
	"Save name": "保存名称",
	"Rename block": "重命名节点",
	"Open documentation": "打开文档",
	"Open docs": "打开文档",
	"Unable to load preview": "无法加载预览",
	"Show additional fields": "显示更多字段",
	"Hide additional fields": "隐藏更多字段",
	"Additional fields": "更多字段",
	Connections: "连接",
	"Expand connections": "展开连接",
	"Collapse connections": "收起连接",

	// -- the console at the canvas floor, and one entry's output
	"Resize terminal": "调整控制台高度",
	"Sort by timestamp": "按时间排序",
	"Sort by time": "按时间排序",
	"Component Playground": "组件试验场",
	"Export console CSV": "导出控制台 CSV",
	"Clear console": "清空控制台",
	"Clear Console": "清空控制台",
	"Terminal options": "控制台选项",
	"Open on run": "运行时自动打开",
	"Open on Run": "运行时自动打开",
	"No logs yet": "暂无日志",
	"Unknown error": "未知错误",
	"Unknown Block": "未知节点",
	"Error in {block}.": "错误出现在 {block}。",
	"Please fix this.": "请修复它。",
	"Resize output panel": "调整输出面板宽度",
	"Show output": "显示输出",
	Output: "输出",
	"Show input": "显示输入",
	Input: "输入",
	"Search in output": "在输出中搜索",
	"Close search": "关闭搜索",
	"Copy output": "复制输出",
	"Structured view": "结构化视图",
	"Structured View": "结构化视图",
	"Wrap text": "自动换行",
	"Wrap Text": "自动换行",
	"Copy Selection": "复制所选",
	"Copy All": "复制全部",
	"Copy Run ID": "复制运行 ID",
	"Fix in Chat": "在对话中修复",
	"Filter by Block": "按节点筛选",
	"Filter by Status": "按状态筛选",

	// -- the floating chat that runs the workflow
	"Add chat inputs to Start block": "把对话输入加到 Start 节点",
	"Add inputs": "添加输入",
	Outputs: "输出",
	"Select outputs": "选择输出",
	"1 output": "1 项输出",
	"{count} outputs": "{count} 项输出",
	"No messages yet": "暂无消息",
	"File upload error": "附件上传失败",
	"Drop files here...": "把文件拖到这里…",
	"Type a message...": "输入消息…",
	"Workflow execution failed": "工作流运行失败",
	"Workflow execution failed.": "工作流运行失败。",
	"Error: {message}": "错误：{message}",
	"{name} is too large (max 10MB)": "{name} 超出大小上限（最大 10MB）",
	"{name} already added": "{name} 已经添加过了",

	// -- the floating variables panel
	Variables: "变量",
	"Add new variable": "添加变量",
	"Close variables panel": "关闭变量面板",
	"Delete Variable": "删除变量",
	"Delete {name}": "删除 {name}",
	"Variable {n}": "变量 {n}",
	"variable {n}": "变量 {n}",
	"No variables yet": "暂无变量",
	"Variable name cannot be empty": "变量名不能为空",
	"Two variables cannot have the same name": "两个变量不能同名",
	Value: "值",
	Plain: "文本",
	Object: "对象",
	Array: "数组",
	"Plain text value": "纯文本值",

	// -- deployment: the chip beside Run, and what it refuses
	Deploy: "部署",
	"Deploying...": "部署中…",
	"Update deployment": "更新部署",
	"Active deployment": "当前部署",
	"Live version": "线上版本",
	"Cannot deploy an empty workflow": "空工作流无法部署",
	"Admin permissions required": "需要管理员权限",
	"Workflow is still loading": "工作流仍在加载",
	"Syncing deployment state...": "正在同步部署状态…",
	"Deployment is already in progress.": "已有部署正在进行。",
	"Pre-deploy validation failed": "部署前校验未通过",
	"Failed to deploy workflow": "部署工作流失败",
	"Failed to redeploy workflow": "重新部署工作流失败",
	"Failed to undeploy workflow": "取消部署失败",
	"Workflow deployed": "工作流已部署",
	"Workflow redeployed": "工作流已重新部署",
	"Workflow undeployed": "工作流已取消部署",
	"Deployment succeeded, but local sync is still catching up. Refresh if the status looks stale.":
		"部署成功，但本地同步还在跟进。若状态看起来不对，请刷新。",
	"Deployment succeeded, but local sync failed. Refresh if the status looks stale.":
		"部署成功，但本地同步失败。若状态看起来不对，请刷新。",

	// -- the deployment modal
	"Workflow Deployment": "工作流部署",
	"Configure and manage workflow deployment and chat settings.": "配置和管理工作流部署与聊天设置。",
	"Deployment settings": "部署设置",
	General: "通用",
	API: "API",
	MCP: "MCP",
	Undeploy: "取消部署",
	"Undeploying...": "正在取消部署…",
	"Undeploy API": "取消部署 API",
	"Are you sure you want to undeploy this workflow? ": "确定要取消部署这个工作流吗？",
	"This will remove the API endpoint and make it unavailable to external users.":
		"这会移除 API 端点，外部调用将不可用。",
	Retrying: "重试中",
	"Retrying automatically. The current version stays live until cutover completes.":
		"正在自动重试。切换完成前，当前版本继续对外提供服务。",
	"Retrying automatically. The workflow goes live once activation completes.": "正在自动重试。激活完成后工作流即上线。",
	"A new version is being prepared. The current version stays live until cutover completes.":
		"正在准备新版本。切换完成前，当前版本继续对外提供服务。",
	"Triggers and schedules are being registered. The workflow goes live once activation completes.":
		"正在注册触发器和调度。激活完成后工作流即上线。",
	"Deployment preparation failed.": "部署准备失败。",
	"The previously deployed version is still live.": "此前部署的版本仍在提供服务。",
	"The workflow remains undeployed.": "工作流仍未部署。",

	// -- the deployment modal's General tab: preview and version list
	"Live Workflow": "线上工作流",
	"See preview": "查看预览",
	"Workflow preview": "工作流预览",
	"Visual preview of the selected workflow version.": "所选工作流版本的画布预览。",
	"Deploy your workflow to see a preview": "部署工作流后即可看到预览",
	Versions: "版本",
	"No deployments yet": "暂无部署",
	Version: "版本",
	"Deployed by": "部署人",
	Timestamp: "时间",
	Inactive: "未启用",
	live: "线上",
	deploying: "部署中",
	failed: "失败",
	"Load Deployment": "载入部署版本",
	"Load deployment": "载入部署版本",
	"Are you sure you want to load ": "确定要载入 ",
	"This will replace your current workflow with the deployed version.": "这会用该部署版本覆盖你当前的工作流。",
	"Promote to live": "设为线上版本",
	"Are you sure you want to promote ": "确定要把 ",
	" to live? This version will become the active deployment and serve all API requests.":
		" 设为线上版本吗？该版本将成为当前部署，处理全部 API 请求。",
	"Edit description": "编辑说明",
	"Add description": "添加说明",
	"Version Description": "版本说明",
	"Edit the description for": "编辑版本说明：",
	"Add a description for": "为此版本添加说明：",
	"Description exceeds the {limit}-character limit (currently {length})": "说明超过 {limit} 字上限（当前 {length} 字）",
	"Describe the changes in this deployment version...": "描述这个部署版本改了什么…",

	// -- the deployment modal's API tab
	"Edit API Info": "编辑 API 信息",
	"Generate API Key": "生成 API 密钥",
	"Workspace API keys": "工作区 API 密钥",
	"Personal API keys": "个人 API 密钥",
	"Describe what this workflow API does...": "描述这个工作流 API 做什么…",
	"Anyone can call this API without authentication. You will be billed for all usage.":
		"任何人都可以免鉴权调用这个 API，所有用量都会计入你的账单。",
	"Requires a valid API key to call this endpoint.": "调用此端点需要有效的 API 密钥。",
	"Failed to update access settings": "更新访问设置失败",
	"Run workflow (stream response)": "运行工作流（流式返回）",
	"Run workflow (async)": "运行工作流（异步）",
	"Copy command": "复制命令",
	"Start Execution": "发起运行",
	"Check Status": "查询状态",
	"Usage Limits": "用量上限",

	// -- the deployment modal's Chat tab
	"Launch Chat": "上线对话",
	Launched: "已上线",
	"Launching...": "上线中…",
	Updated: "已更新",
	Title: "标题",
	"Customer Support Assistant": "客服助手",
	"Select which block outputs to use": "选择要使用的节点输出",
	"Include thinking": "包含思考过程",
	"Include tool calls": "包含工具调用",
	"Welcome message": "欢迎语",
	"Enter a welcome message for your chat": "填写对话的欢迎语",
	"This message will be displayed when users first open the chat": "用户首次打开对话时会看到这句话",
	"Hi there! How can I help you today?": "你好！有什么可以帮你的？",
	"Identifier is required": "必须填写标识",
	"Identifier can only contain lowercase letters, numbers, and hyphens": "标识只能包含小写字母、数字和连字符",
	"Title is required": "必须填写标题",
	"Password is required when using password protection": "使用密码保护时必须设置密码",
	"Password cannot contain only whitespace": "密码不能只由空白字符组成",
	"At least one email or domain is required when using SSO access control":
		"使用 SSO 访问控制时，至少要填写一个邮箱或域名",
	"At least one email or domain is required when using email access control":
		"使用邮箱访问控制时，至少要填写一个邮箱或域名",
	"Please select at least one output block": "请至少选择一个输出节点",
	"Please wait for identifier validation to complete": "请等待标识校验完成",
	"An unexpected error occurred while deleting": "删除时发生了意外错误",
	"Name is available": "该名称可用",
	"Live at:": "线上地址：",
	"The unique URL path where your chat will be accessible": "对话对外访问的唯一路径",
	"Access control": "访问控制",
	"Enter password": "输入密码",
	"Enter new password to change": "要修改请输入新密码",
	"Leave empty to keep the current password": "留空表示保持当前密码",
	"This password will be required to access your chat": "访问对话时需要输入这个密码",
	"Failed to load the current password": "无法读取当前密码",
	"Allowed SSO emails": "允许的 SSO 邮箱",
	"Change deployment password": "修改部署密码",
	"Change deployment password?": "要修改部署密码吗？",
	"Are you sure you want to change the password for this deployment?": "确定要修改这个部署的密码吗？",
	"Change Password and Redeploy": "修改密码并重新部署",
	"Delete Chat": "删除对话",
	"this chat": "这个对话",
	'This will remove the chat at "{url}" and make it unavailable to all users.':
		"这会移除 {url} 上的对话，所有用户都将无法访问。",

	// -- the deployment modal's MCP tab
	"Deploy your workflow first to add it as an MCP tool.": "请先部署工作流，才能把它加为 MCP 工具。",
	"Create an MCP Server to expose your workflows as tools.": "创建 MCP 服务器，把工作流暴露为工具。",
	"Create MCP Server": "创建 MCP 服务器",
	"Tool name": "工具名称",
	"e.g., book_flight": "例如 book_flight",
	"Use lowercase letters, numbers, and underscores only": "只能使用小写字母、数字和下划线",
	"Use lowercase letters and numbers, separated by single underscores": "只能使用小写字母和数字，并以单个下划线分隔",
	"Tool name must be {max} characters or fewer": "工具名称不能超过 {max} 个字符",
	"Describe what this tool does...": "描述这个工具做什么…",
	"Defaults to the workflow description: {description}": "默认使用工作流说明：{description}",
	"Parameters ({count})": "参数（{count}）",
	"Descriptions default to your Start block inputs; edit to override for this tool.":
		"参数说明默认取自 Start 节点的输入；可在此为该工具单独覆盖。",
	"Describe {name}": "描述 {name}",
	Servers: "服务器",
	"Select servers...": "选择服务器…",
	"Search servers...": "搜索服务器…",
	"1 server": "1 个服务器",
	"{count} servers selected": "已选择 {count} 个服务器",
	"Select a server to save this tool": "选择一个服务器才能保存此工具",
	"Save Tool": "保存工具",
	"Enter a tool name to select servers": "填写工具名称后才能选择服务器",
	"Fix the tool name to select servers": "修正工具名称后才能选择服务器",

	// -- the block card on the canvas
	Locked: "已锁定",
	disabled: "已停用",
	"This is a legacy block. Click to upgrade": "这是旧版节点，点击升级",
	"This block is no longer supported. Click to replace": "此节点已不再支持，点击替换",
	"{model} is no longer available. Click to switch models": "{model} 已不再提供，点击更换模型",
	"{model} is a legacy model. Click to upgrade": "{model} 是旧版模型，点击升级",
	"Edit access required to fix": "需要编辑权限才能修复",
	"Admin permission required to deploy": "需要管理员权限才能部署",
	"Click to deploy": "点击部署",
	"Click to redeploy": "点击重新部署",
	undeployed: "未部署",
	redeploy: "待重新部署",
	"Click to reactivate": "点击重新启用",
	Webhook: "Webhook",
	"{provider} Webhook": "{provider} Webhook",
	"Path: {path}": "路径：{path}",
	"This workflow is triggered by a webhook.": "此工作流由 Webhook 触发。",
	Context: "上下文",
	"Route {n}": "分支 {n}",
	"On error": "出错时",
	"On error branch": "出错分支",
	"Select {name}": "选择 {name}",
	"Code preview": "代码预览",

	// -- a note on the canvas
	Note: "便签",
	"Note title": "便签标题",
	"Edit note title": "编辑便签标题",
	"Edit note content": "编辑便签内容",
	"{name} content": "{name} 的内容",
	"Expand note": "展开便签",
	"Collapse note": "收起便签",

	// -- one block's fields, in the editor tab
	"Copy value": "复制值",
	"Generate content with AI": "用 AI 生成内容",
	"Generate code with AI": "用 AI 生成代码",
	"Generate with AI...": "用 AI 生成…",
	"Switch to manual ID": "切换到手动填 ID",
	"Switch to manual value": "切换到手动填值",
	"Switch to JSON": "切换到 JSON",
	"Switch to file uploader": "切换到文件上传",
	"View table": "查看数据表",
	"View knowledge base": "查看知识库",
	"Select an option...": "选择一项…",
	"Type or select an option...": "输入或选择一项…",
	"Create Sandbox": "创建沙箱",
	"Retry on fail": "失败后重试",
	"Max tries": "最多尝试次数",
	"Wait between tries (ms)": "两次尝试之间等待（毫秒）",
	"Write JavaScript...": "编写 JavaScript…",
	"Write Python...": "编写 Python…",
	"Write shell commands...": "编写 shell 命令…",
	"Describe the JSON schema to generate...": "描述要生成的 JSON Schema…",
	"Describe the JSON object to generate...": "描述要生成的 JSON 对象…",
	"Describe the JavaScript code to generate...": "描述要生成的 JavaScript 代码…",
	"Describe the Python script you want to create...": "描述你想写的 Python 脚本…",
	"Describe the shell commands you want to run...": "描述你想运行的 shell 命令…",

	// -- field lists: input format, response format, evaluator metrics
	Field: "字段",
	"Add {title}": "添加{title}",
	String: "字符串",
	"Enter default value": "填写默认值",
	"Enter return value": "填写返回值",
	"Describe this field": "描述这个字段",
	"Min Value": "最小值",
	"Max Value": "最大值",
	"Add Metric": "添加指标",
	"Delete Metric": "删除指标",
	Accuracy: "准确度",
	"How accurate is the response?": "回答有多准确？",

	// -- conditions, routes and subflows
	"Move Up": "上移",
	"Move Down": "下移",
	"Delete Block": "删除节点",
	"Delete Route": "删除分支",
	"Delete Condition": "删除条件",
	"Describe when this route should be taken...": "描述什么情况下走这条分支…",
	"Loop Type": "循环类型",
	"Parallel Type": "并行类型",
	"Loop Iterations": "循环次数",
	"Parallel Iterations": "并行数量",
	"Collection Items": "循环集合",
	"Parallel Items": "并行集合",
	"While Condition": "循环条件",
	"For Loop": "计数循环",
	"For Each": "遍历循环",
	"While Loop": "条件循环",
	"Do While Loop": "先执行的条件循环",
	"Parallel Count": "按数量并行",
	"Parallel Each": "按集合并行",
	"Select type...": "选择类型…",

	// -- the agent block's tools
	"Add tool...": "添加工具…",
	"Search tools...": "搜索工具…",
	"No tools found": "没有找到工具",
	"Create Tool": "创建工具",
	"Add MCP Server": "添加 MCP 服务器",
	"Custom Tools": "自定义工具",
	"Built-in Tools": "内置工具",
	"MCP Servers": "MCP 服务器",
	Advanced: "高级",
	"MCP Server (Advanced)": "MCP 服务器（高级）",
	"Use all available tools": "使用该服务器的全部工具",
	"{name} ({count} tools)": "{name}（{count} 个工具）",
	"Unknown Server": "未知服务器",
	"Unknown Tool": "未知工具",
	"Custom tools are not supported by this block yet": "此节点暂不支持自定义工具",
	"MCP tools are not supported by this block yet": "此节点暂不支持 MCP 工具",
	"Tool usage control": "工具调用方式",
	Auto: "自动",
	Force: "强制",
	"(model decides)": "（由模型决定）",
	"(always use)": "（每次都调用）",
	"(not supported by model)": "（该模型不支持）",
	"Remove tool": "移除工具",
	"Remove all from {name}": "移除 {name} 的全部工具",
	server: "服务器",
	Operation: "操作",
	"Select operation": "选择操作",
	"Create Agent Tool": "创建智能体工具",
	"Edit Agent Tool": "编辑智能体工具",
	Schema: "结构",
	"Update Tool": "更新工具",
	"Delete Custom Tool": "删除自定义工具",
	"Schema cannot be empty": "结构定义不能为空",
	"Failed to load tool data. Please try again.": "工具数据加载失败，请重试。",
	"Failed to save custom tool": "保存自定义工具失败",
	"Couldn't delete tool": "删除工具失败",
	Next: "下一步",

	// -- skills
	"Add skill...": "添加技能…",
	"No skills found": "没有找到技能",
	"Create Skill": "创建技能",
	"Remove skill": "移除技能",

	// -- selectors and pickers
	"Select credential": "选择凭据",
	"Search credentials...": "搜索凭据…",
	"Service accounts": "服务账号",
	"Fill in required fields above first": "请先填写上面的必填项",
	"Select MCP server": "选择 MCP 服务器",
	"Select tool": "选择工具",
	"Select server first": "请先选择服务器",
	"Select knowledge base": "选择知识库",
	"Select knowledge bases": "选择知识库",
	"Select a table": "选择数据表",
	"Select workflow...": "选择工作流…",
	"Select resource": "选择资源",
	"Select channels": "选择频道",
	"Select time": "选择时间",
	"Workspace root": "工作区根目录",
	"Anywhere in the workspace": "工作区内任意位置",
	"No folders found": "没有找到文件夹",
	"Select a variable...": "选择一个变量…",
	"{type} value": "{type} 值",
	"Enter value or reference": "填写值或引用",
	"Create Secret": "创建密钥",

	// -- files
	"Select or upload file": "选择或上传文件",
	"Loading files...": "正在加载文件…",
	"Upload New File": "上传新文件",
	"+ Add More": "+ 继续添加",
	"Upload complete!": "上传完成！",

	// -- knowledge tags and filters
	"Tag Filters": "标签筛选",
	"No filters": "无筛选",
	"{count} filter(s) applied": "已应用 {count} 个筛选",
	"Filter {n}": "筛选 {n}",
	"Add Filter": "添加筛选",
	"Delete Filter": "删除筛选",
	"Select operator": "选择运算符",
	"No tags": "无标签",
	"{count} tag(s) configured": "已配置 {count} 个标签",

	// -- table filters and sorts
	"Condition {n}": "条件 {n}",
	"Sort {n}": "排序 {n}",
	"Select column": "选择列",
	"Select direction": "选择方向",

	// -- chat messages field
	"Message {n}": "消息 {n}",
	"Select message role": "选择消息角色",
	"Delete message": "删除消息",
	"Move message up": "上移消息",
	"Move message down": "下移消息",
	"Add message below": "在下方添加消息",
	"Enter message content...": "填写消息内容…",

	// -- PII detection
	"Select PII Types to Detect": "选择要检测的个人信息类型",
	"PII types": "个人信息类型",
	"Choose which types of personally identifiable information to detect and block.":
		"选择要检测并拦截的个人身份信息类型。",

	// -- the Slack app setup wizard
	"Setup Slack App": "配置 Slack 应用",
	"Click to copy manifest": "点击复制 manifest",
	"Deploy once to lock in the webhook URL": "先部署一次以确定 Webhook 地址",
	"Couldn't copy manifest — copy it manually from the developer console.":
		"无法复制 manifest — 请到开发者控制台手动复制。",
	"Your Slack app is set up. Save the workflow and Slack will verify the webhook URL automatically.":
		"Slack 应用已配置好。保存工作流后，Slack 会自动校验 Webhook 地址。",
	"Click Done and save this workflow.": "点击完成并保存这个工作流。",
	"(not saved yet)": "（尚未保存）",

	// -- the canvas: what it refuses, and why
	"Cannot delete locked blocks or blocks inside locked containers": "无法删除已锁定的节点，或位于锁定容器内的节点",
	"Skipped {count} protected block(s)": "已跳过 {count} 个受保护的节点",
	"Cannot connect to locked blocks or blocks inside locked containers":
		"无法连接到已锁定的节点，或位于锁定容器内的节点",
	"Cannot remove connections to locked blocks": "无法删除通往已锁定节点的连接",
	"Cannot move locked blocks out of locked containers": "无法把已锁定的节点移出锁定容器",
	"Cannot place a container inside one of its own nested containers": "无法把容器放进它自己的下级容器里",
	"Cannot paste a subflow inside itself or its own descendant.": "无法把子流程粘贴到它自己或它的下级里。",
	"Triggers cannot be placed inside loop or parallel subflows.": "触发器不能放在循环或并行子流程里。",
	"Cannot add new trigger blocks when a legacy Start block exists. Available in newer workflows.":
		"存在旧版 Start 节点时无法添加新的触发器节点；新建的工作流可以使用。",
	"Cannot paste trigger blocks when a legacy Start block exists.": "存在旧版 Start 节点时无法粘贴触发器节点。",
	"Cannot duplicate trigger blocks when a legacy Start block exists.": "存在旧版 Start 节点时无法复制触发器节点。",
	"A workflow can only have one {name} trigger block. Please remove the existing one before adding a new one.":
		"一个工作流只能有一个 {name} 触发器节点，请先删除已有的再添加。",
	"A workflow can only have one {name} trigger block. Please remove the existing one before pasting.":
		"一个工作流只能有一个 {name} 触发器节点，粘贴前请先删除已有的。",
	"A workflow can only have one {name} trigger block. Cannot duplicate.":
		"一个工作流只能有一个 {name} 触发器节点，无法复制。",
	"A workflow can only have one {name} block. Please remove the existing one before adding a new one.":
		"一个工作流只能有一个 {name} 节点，请先删除已有的再添加。",
	"A workflow can only have one {name} block. Please remove the existing one before pasting.":
		"一个工作流只能有一个 {name} 节点，粘贴前请先删除已有的。",
	"A workflow can only have one {name} block. Cannot duplicate.": "一个工作流只能有一个 {name} 节点，无法复制。",
	trigger: "触发器",
	"Cannot run from inside subflow": "无法从子流程内部开始运行",
	"This workflow is locked": "此工作流已锁定",
	"This workflow is locked. Ask an admin to unlock it.": "此工作流已锁定，请联系管理员解锁。",
	'This workflow is locked by folder "{name}"': "此工作流被文件夹「{name}」锁定",
	"This workflow is locked by a parent folder": "此工作流被上级文件夹锁定",
	"Unlock Workflow": "解锁工作流",
	"Unlock the workflow to add blocks.": "解锁工作流后才能添加节点。",
	"Workflow was modified. Run the workflow again to enable running from block.":
		"工作流已修改，请重新运行一次，才能从节点开始运行。",

	// -- the connected-block picker on an edge
	"Add a connected block": "添加下游节点",
	"Add block": "添加节点",
	"Close block selector": "关闭节点选择器",
	"Search blocks": "搜索节点",
	"Search blocks...": "搜索节点…",
	"Recently used": "最近使用",
	Popular: "常用",
	"All blocks": "全部节点",

	// -- search and replace
	"Snapshot view is readonly": "快照视图为只读",
	"Show replace controls": "显示替换控件",
	"Hide replace controls": "隐藏替换控件",
	"{index} of {total}": "第 {index} 项，共 {total} 项",
	"Choose replacement...": "选择替换内容…",
	"No valid replacements available": "没有可用的替换项",
	"Replace All": "全部替换",
	"{count} replaceable match": "{count} 处可替换",
	"{count} replaceable matches": "{count} 处可替换",
	"No current match.": "当前没有匹配项。",
	"No replaceable matches.": "没有可替换的匹配项。",
	"No eligible matches to replace.": "没有符合条件的可替换项。",
	"Replacement stopped: {reason}": "替换已停止：{reason}",
	"Replacement stopped: {count} match changed. Re-run search and try again.":
		"替换已停止：有 {count} 处匹配已变化，请重新搜索后再试。",
	"Replacement could not be applied in the current workflow state.": "当前工作流状态下无法应用替换。",

	// -- a note on the canvas
	"Add note, or press '/' for commands…": "写点备注，或按「/」调出命令…",

	// -- empty and placeholder states inside a block's fields
	"This block has no subblocks": "此节点没有可配置项",
	"None selected": "未选择",
	"All selected": "已全选",
	"Configure PII Types": "配置个人信息类型",
	"Select all entities": "全选所有类型",
	"No workflow selected": "尚未选择工作流",
	"Select a workflow above to configure inputs": "请先在上面选择工作流，再配置输入",
	"No inputs available": "没有可配置的输入",
	"Select a workflow to configure its inputs": "选择一个工作流以配置它的输入",
	"This workflow has no custom input fields": "此工作流没有自定义输入字段",
	"Select a tool to configure its parameters": "选择一个工具以配置它的参数",
	"This tool requires no parameters": "此工具不需要参数",
	"No matching tags found": "没有匹配的标签",
	"No variable assignments defined": "尚未定义变量赋值",
	"Add variables in the Variables panel to get started": "先在变量面板中添加变量",
	"No variables available": "没有可用的变量",
	Variable: "变量",
	"Add Variable": "添加变量",
	"No tags defined for this knowledge base": "此知识库尚未定义标签",
	"Define tags at the knowledge base level first": "请先在知识库层级定义标签",
	Logic: "逻辑",
	Direction: "方向",
	"Add sort": "添加排序",
	"Add Sort": "添加排序",
	"Delete Sort": "删除排序",
	"Add Condition": "添加条件",
	"Add filter condition": "添加筛选条件",
	"Delete {title}": "删除{title}",
	"Parallel Batch Size": "并行批大小",
	"Choose one available folder to upload a new file. Existing files can still be selected.":
		"上传新文件时只能选择一个可用文件夹；已有文件仍可继续选择。",
	"No blocks found.": "没有找到节点。",
	canceled: "已取消",
	"Enter a number": "请输入数字",
};
